import { CodeNext, ValidityDeterminate } from '@fusion-advantage/backcompat'
import { cidForDirItemId, formCollaboratorMustSign, CollaboratingPhysicianSignatureNameConcept, ExecutableCalc, CalcType, ExecutableCalcCalculateOk, CalcRuntimeError, ExecutableCalcCalculateFunction } from "@fusion-advantage/formlib"
import _ from "lodash"
import { DateTime } from 'luxon'
import { ok, Result } from 'neverthrow'

const cidEvaluatorSignature = cidForDirItemId(391)
const cidCollaboratorSignature = cidForDirItemId(393)
const cidCollaboratorNotes = cidForDirItemId(1329)

export const createEvaluatorSignatureValidationCalc = (): ExecutableCalc => {
	const calculate: ExecutableCalcCalculateFunction = (context): Result<ExecutableCalcCalculateOk, CalcRuntimeError> => {
		const value = context.statusValue(cidEvaluatorSignature)
		if(!value) {
			return ok({
				calcType: CalcType.Validation,
				returnValue: { determinate: ValidityDeterminate.Required }
			})
		}

		// When the encounter is back with the clinician after an audit push-back
		// (ClinicianAuditReview), a signature applied *before* the push-back is
		// stale and must be re-applied before it can be submitted back to audit.
		// We validate on the signature's timestamp rather than relying on the
		// submit-time signature wipe having fired, so an unsigned-since-pushback
		// chart is caught regardless of whether the wipe took effect.
		const encounter = context.getExport().Form.Encounter
		if(encounter.CodeNext === CodeNext.ClinicianAuditReview) {
			const signedAt = DateTime.fromISO(context.getStatus(cidEvaluatorSignature)?.updatedAt ?? '')
			const pushedBackAt = DateTime.fromISO(encounter.EncounterAppLastStatusDT)
			// treat a missing/unparseable signature timestamp as stale
			const isStale = !signedAt.isValid || (pushedBackAt.isValid && signedAt < pushedBackAt)
			if(isStale) {
				return ok({
					calcType: CalcType.Validation,
					returnValue: {
						determinate: ValidityDeterminate.Required,
						message: 'Evaluator must sign again after audit review.',
					}
				})
			}
		}

		return ok({
			calcType: CalcType.Validation,
			returnValue: { determinate: ValidityDeterminate.Valid }
		})
	}
	return new ExecutableCalc({
		calcType: CalcType.Validation,
		outputCid: cidEvaluatorSignature,
		inputCids: [cidEvaluatorSignature],
		selectCalculate: () => calculate,
	})
}

export const createCollaboratorNotesValidationCalc = (): ExecutableCalc => {
	const calculate: ExecutableCalcCalculateFunction = (context): Result<ExecutableCalcCalculateOk, CalcRuntimeError> => {
		const codeNext = context.getExport().Form.Encounter.CodeNext
		if(!formCollaboratorMustSign(codeNext)) {
			return ok({
				calcType: CalcType.Validation,
				returnValue: { determinate: ValidityDeterminate.Valid }
			})
		}
		const hasSignature = context.hasAnyValue(cidCollaboratorSignature)
		const hasNotes = context.hasAnyValue(cidCollaboratorNotes)
		if(hasSignature || hasNotes) {
			return ok({
				calcType: CalcType.Validation,
				returnValue: { determinate: ValidityDeterminate.Valid }
			})
		}
		return ok({
			calcType: CalcType.Validation,
			returnValue: {
				determinate: ValidityDeterminate.Required,
				message: 'Collaborator must provide notes when signature has not been provided.',
			}
		})
	}
	return new ExecutableCalc({
		calcType: CalcType.Validation,
		outputCid: cidCollaboratorNotes,
		inputCids: [cidCollaboratorNotes, cidCollaboratorSignature],
		selectCalculate: () => calculate,
	})
}

export const createCollaboratorSignatureValidationCalc = (): ExecutableCalc => {
	const calculate: ExecutableCalcCalculateFunction = (context): Result<ExecutableCalcCalculateOk, CalcRuntimeError> => {
		const codeNext = context.getExport().Form.Encounter.CodeNext
		if(!formCollaboratorMustSign(codeNext)) {
			return ok({
				calcType: CalcType.Validation,
				returnValue: { determinate: ValidityDeterminate.Valid }
			})
		}

		const hasNotes = context.hasAnyValue(cidCollaboratorNotes)
		if(hasNotes) {
			return ok({
				calcType: CalcType.Validation,
				returnValue: { determinate: ValidityDeterminate.Valid }
			})
		}

		const hasSignature = context.hasAnyValue(cidCollaboratorSignature)
		if(!hasSignature) {
			return ok({
				calcType: CalcType.Validation,
				returnValue: {
					determinate: ValidityDeterminate.Required,
					message: 'Collaborator must provide signature when notes have not been provided.',
				}
			})
		}

		return ok({
			calcType: CalcType.Validation,
			returnValue: { determinate: ValidityDeterminate.Valid }
		})
	}
	return new ExecutableCalc({
		calcType: CalcType.Validation,
		outputCid: cidCollaboratorSignature,
		inputCids: [cidCollaboratorSignature, cidCollaboratorNotes],
		selectCalculate: () => calculate,
	})
}

export const createCollaboratorNameValueCalc = (): ExecutableCalc => {
	const calculate: ExecutableCalcCalculateFunction = (context): Result<ExecutableCalcCalculateOk, CalcRuntimeError> => {
		const signature = context.statusValue(cidCollaboratorSignature)
		const exp = context.getExport()
		const collaboratorName = exp.Form.Encounter.AccessingClinicianNameDegree ?? ''
		
		if(signature) {
			return ok({calcType: CalcType.Value, returnValue: [{ value: collaboratorName }]})
		}

		return ok({calcType: CalcType.Value, returnValue: [{ value: null }]})
	}
	return new ExecutableCalc({
		calcType: CalcType.Value,
		outputCid: CollaboratingPhysicianSignatureNameConcept.cid,
		inputCids: [cidCollaboratorSignature],
		selectCalculate: () => calculate,
	})
}
